import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { hapticError, hapticTick } from '../../_utils/haptics';
import { isSelfEmployedMe } from '../permissions/access';
import { seChoicesService } from '../../_services/seChoices.service';
import { marketingService } from '../../_services/marketing.service';
import { marketingBackend } from '../marketing-v2/services/marketingBackend';
import SettingsPageLayout from './SettingsPageLayout';
import { Choices, Saved, Skeleton } from '../onboarding/setup/ui';
import {
  firstWord, loadTeam, myIds, ownerOf, reasonOf, saveProFlags, useAlive, useFlash, useSerial,
} from '../onboarding/setup/data/stylist';
import {
  ClientShareChoice, DiaryChoice, salonAccessLine, useClientShare, useDiaryChoice,
} from '../onboarding/setup/shared/PrivacyChoices';
import '../onboarding/setup/setup.css';
import '../onboarding/setup/chapters/Stylist.css';
import './SettingsModern.css';
import './YourChoices.css';
import { DISCOUNTS_VISIBLE } from '../../config/featureFlags';
import { loadChoices, saveMyChoice, answerOf } from '../discounts/discountStore';
import { money, splitFor } from '../discounts/data/codes';

/**
 * Settings › Your business › Your choices — a self-employed stylist's ONE
 * place for the choices that are theirs at this salon. No new rules: every
 * switch here already exists elsewhere and saves through the same endpoint,
 * which only the stylist can use.
 *
 *   What <salon> can see     client sharing and diary visibility, the very
 *                            switches (and words) of the setup's Your
 *                            privacy chapter (shared/PrivacyChoices)
 *   Joining in with <salon>  salon-run marketing, the salon's loyalty
 *                            scheme (only when it runs one), the salon's
 *                            booking page
 *   Your menu and money      the menu model the owner chose, the salon's
 *                            client sharing with them, payments: read-only
 *
 * Every change saves at once and shows a small "Saved".
 */

/** One instant-save choice: optimistic, last tap wins, back on failure. */
function useInstant(initial, save) {
  const alive = useAlive();
  const serial = useSerial();
  const [flashed, flash] = useFlash();
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => { setValue(initial); }, [initial]);
  const pick = (v) => {
    if (v === value) return;
    const before = value;
    setValue(v);
    setError('');
    setSaving(true);
    serial('v', () => saveRef.current(v))
      .then(() => { if (alive.current) flash('v'); })
      .catch((e) => {
        if (!alive.current) return;
        setValue(before);
        setError(reasonOf(e, 'That didn’t save. Try again.'));
        hapticError();
      })
      .finally(() => { if (alive.current) setSaving(false); });
  };
  return { value, saving, saved: !!flashed.v, error, pick };
}

function Status({ state }) {
  return (
    <>
      <div className="su-stylist-summary" aria-live="polite">
        {state.saving ? <span className="su-dots" aria-label="Saving"><i /><i /><i /></span> : state.saved ? <Saved /> : null}
      </div>
      {state.error && <div className="su-error" role="alert">{state.error}</div>}
    </>
  );
}

function Choice({ title, sub, children }) {
  return (
    <div className="yc-choice">
      <strong>{title}</strong>
      {sub && <small>{sub}</small>}
      <div className="yc-choice-body">{children}</div>
    </div>
  );
}

const PRICE_WORDS = {
  salon: { chip: 'Salon menu, salon prices', line: (s) => `${s} uses one menu, with the same prices for everyone.` },
  salonOwnPrices: { chip: 'Salon menu, my prices', line: (s) => `${s} uses one menu. You set your own prices for it.` },
  own: { chip: 'My own menu', line: () => 'You run your own menu and set your own prices.' },
};
const PAY_WORDS = {
  connected: { chip: 'Connected', tone: 'ok' },
  not_set_up: { chip: 'Not set up', tone: 'warn' },
  action_needed: { chip: 'Needs details', tone: 'warn' },
  unknown: { chip: 'Can’t tell just now', tone: 'grey' },
};

function marketingSave(me) {
  const m = me.choices.marketing;
  return async (v) => {
    const res = await marketingService.updateMarketingSetting({
      hairdresserId: me.userId,
      // Their own answer only; the salon's is sent back as it stands.
      salonConsent: m.offered ? 1 : 0,
      selfEmpConsent: v,
      controlledSettingId: m.controlledSettingId,
    });
    const d = res && res.data;
    if (!d || d.statusCode !== 1) {
      const e = new Error((d && d.error && (d.error.responseMessage || d.error.message)) || 'That didn’t save. Try again.');
      e.plain = true;
      throw e;
    }
  };
}

function YourChoicesBody({ user }) {
  const ids = useMemo(() => myIds(user), [user]);
  const [meP] = useState(() => seChoicesService.mine());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [owner, setOwner] = useState({ first: '', id: null });
  // Discount codes: their answer on the salon's codes, and their split (for
  // the worked example; sharing means nothing when the salon takes 0%)
  const [codesAnswer, setCodesAnswer] = useState(undefined);
  const [myCut, setMyCut] = useState(null);

  useEffect(() => {
    let off = false;
    meP.then((d) => { if (!off) setData(d); }).catch((e) => { if (!off) setError(reasonOf(e, 'I couldn’t load your choices just now.')); });
    loadTeam().then((rows) => {
      const o = ownerOf(rows);
      if (!off && o && !ids.includes(String(o._id))) setOwner({ first: firstWord(o.name), id: String(o._id) });
      const mine = (rows || []).find((r) => ids.includes(String(r._id)));
      if (!off && mine && mine.hairdresserInfo) setMyCut(Number(mine.hairdresserInfo.salonCommissionRate) || 0);
    }).catch(() => { /* the salon's name instead */ });
    if (DISCOUNTS_VISIBLE) {
      loadChoices().then((all) => {
        if (off) return;
        const mineId = ids.find((id) => all[String(id)]);
        setCodesAnswer(mineId ? answerOf(all[String(mineId)]) : null);
      }).catch(() => { if (!off) setCodesAnswer(null); });
    }
    return () => { off = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The two privacy choices: the setup chapter's own hooks and switches.
  const share = useClientShare({
    ids, user,
    load: () => meP.then((d) => ({ clientShare: d.me.choices.clients.share, salonClientAccess: d.me.choices.salonClientAccess, cached: false })),
  });
  const diaryChoice = useDiaryChoice({ ids });

  const me = data && data.me;
  const c = me ? me.choices : null;
  const marketing = useInstant(c ? (c.marketing.accepted ? 1 : 0) : null, me ? marketingSave(me) : () => Promise.resolve());
  const loyalty = useInstant(c && c.loyalty ? (c.loyalty.joined ? 1 : 0) : null, (v) => marketingBackend.loyaltyJoin(v === 1).catch((e) => { e.plain = true; throw e; }));
  const booking = useInstant(c ? (c.bookingPage.shown ? 1 : 0) : null, (v) => saveProFlags({ showOnSalonProfile: v === 1 }));
  const codes = useInstant(codesAnswer === undefined ? null : codesAnswer, (v) => saveMyChoice(v));

  const salonName = (data && data.salon && data.salon.name) || '';
  const salon = salonName || 'the salon';
  const Salon = salonName || 'The salon';
  const ownerName = owner.first || 'The owner';

  if (error && !data) {
    return <div className="settings-card"><p className="yc-muted">{error}</p></div>;
  }

  const prices = data && PRICE_WORDS[data.salon.menuModel];
  const pay = c ? (PAY_WORDS[c.payments] || PAY_WORDS.unknown) : null;
  const access = share.clients ? share.clients.salonClientAccess : (c ? c.salonClientAccess : null);

  return (
    <div className="yc-page">
      <div className="yc-crumb">Settings › Your business › <b>Your choices</b></div>
      <div className="yc-bailey">
        <div className="yc-face" aria-hidden="true">B</div>
        <p>These are your choices. {Salon} can see what you’ve picked, but only you can change them.</p>
      </div>

      <section className="settings-card yc-card" aria-labelledby="yc-see">
        <h2 className="yc-glabel" id="yc-see">What {salon} can see</h2>
        <Choice title="Who can see your clients?" sub={`Your clients are yours. Choose whether ${salon} can see them too.`}>
          <ClientShareChoice share={share} salon={salon} salonTitle={Salon} ownerFirst={owner.first} showAccess={false} />
        </Choice>
        <Choice title="Who can see your diary?" sub={`Whoever can see it can also book clients in with you. ${ownerName} can always see it. The front desk follows this too.`}>
          <DiaryChoice
            choice={diaryChoice}
            ids={ids}
            ownerId={owner.id}
            salon={salon}
            ownerName={ownerName}
            onRetry={() => { hapticTick(); diaryChoice.retry(); }}
            missingNote="Your diary settings aren’t ready here yet. Try again in a moment."
          />
        </Choice>
      </section>

      <section className="settings-card yc-card" aria-labelledby="yc-join">
        <h2 className="yc-glabel" id="yc-join">Joining in with {salon}</h2>
        {!c ? <Skeleton h={120} style={{ borderRadius: 16 }} /> : (
          <>
            <Choice title="Marketing to my clients" sub="Your clients only get the salon’s emails and texts if you say so.">
              {c.marketing.controlledSettingId ? (
                <>
                  <Choices
                    compact
                    label="Marketing to my clients"
                    value={marketing.value}
                    onChange={marketing.pick}
                    options={[
                      { value: 1, label: `${Salon} markets to them` },
                      { value: 0, label: 'I’ll market to my own' },
                    ]}
                  />
                  {!c.marketing.offered && marketing.value === 1 && (
                    <p className="yc-note">{Salon} hasn’t switched this on yet, so for now you still market to your own clients.</p>
                  )}
                  <Status state={marketing} />
                </>
              ) : (
                <p className="yc-note">{Salon} hasn’t offered to run your marketing, so you market to your own clients.</p>
              )}
            </Choice>

            {c.loyalty && (
              <Choice title="Loyalty" sub={`${Salon} runs a loyalty scheme${data.salon.loyaltyScheme && data.salon.loyaltyScheme.name ? `, ${data.salon.loyaltyScheme.name}` : ''}. Join it and your clients earn points with you too.`}>
                {c.loyalty.offered ? (
                  <>
                    <Choices
                      compact
                      label="Loyalty"
                      value={loyalty.value}
                      onChange={loyalty.pick}
                      options={[
                        { value: 1, label: `Join ${salon}’s scheme` },
                        { value: 0, label: 'Not for me' },
                      ]}
                    />
                    <Status state={loyalty} />
                  </>
                ) : (
                  <p className="yc-note">{Salon} hasn’t offered its scheme to you yet. When it does, you can join here.</p>
                )}
              </Choice>
            )}

            {DISCOUNTS_VISIBLE && (
              <Choice title={`${Salon}’s discount codes`} sub={`Can ${salon}’s codes be used on your services? Your own codes are always yours.`}>
                {codesAnswer === undefined ? <Skeleton h={120} style={{ borderRadius: 16 }} /> : (
                  <>
                    <Choices
                      compact
                      columns={1}
                      label={`${Salon}’s discount codes`}
                      value={codes.value}
                      onChange={codes.pick}
                      options={[
                        { value: 'salon', label: 'Only when the business owner pays', hint: 'You always get your full share. The business owner covers the discount.' },
                        ...(myCut === 0 ? [] : [{
                          value: 'shared',
                          label: 'Yes, and we can share the cost',
                          hint: (() => {
                            const cut = (myCut == null ? 40 : myCut) / 100;
                            const shared = splitFor({ price: 100, discount: 20, salonCut: cut, mode: 'shared' });
                            const full = splitFor({ price: 100, discount: 20, salonCut: cut, mode: 'salon' });
                            return `In line with your split. On a £100 service with a £20 code, you’d get ${money(shared.stylist)} instead of ${money(full.stylist)}.`;
                          })(),
                        }]),
                        { value: 'no', label: 'No', hint: 'Their codes won’t work on your services.' },
                      ]}
                    />
                    {codes.value === null && <p className="yc-note">Until you choose, {salon}’s codes don’t work on your services.</p>}
                    {myCut === 0 && <p className="yc-note">{Salon} doesn’t take a share of your takings, so there’s nothing to share. The business owner pays for any code they run on your services.</p>}
                    <Status state={codes} />
                  </>
                )}
              </Choice>
            )}

            <Choice title={`Show me on ${salon}’s booking page`} sub="Clients can pick you when they book online with the salon.">
              <Choices
                compact
                label={`Show me on ${salon}’s booking page`}
                value={booking.value}
                onChange={booking.pick}
                options={[
                  { value: 1, label: 'Show me' },
                  { value: 0, label: 'Hide me' },
                ]}
              />
              <Status state={booking} />
            </Choice>
          </>
        )}
      </section>

      <section className="settings-card yc-card" aria-labelledby="yc-money">
        <h2 className="yc-glabel" id="yc-money">Your menu and money</h2>
        {!c ? <Skeleton h={120} style={{ borderRadius: 16 }} /> : (
          <>
            <Choice title="My services and prices" sub={prices ? prices.line(Salon) : 'Your services and prices are on your menu.'}>
              {prices && <span className="yc-chip yc-chip--blue">{prices.chip}</span>}
              <Link className="yc-link" to="/new-services">Change my prices</Link>
            </Choice>
            <Choice title="Seeing the salon’s clients" sub={access === null || access === undefined ? `Set by ${salon}.` : salonAccessLine(access, { salon, Salon, ownerFirst: owner.first })}>
              <span className={`yc-chip ${access === 1 ? 'yc-chip--ok' : 'yc-chip--grey'}`}>{access === 1 ? 'Allowed' : 'Not shared'}</span>
            </Choice>
            <Choice title="Getting paid" sub="Card payments for your bookings land in your own account.">
              <span className={`yc-chip yc-chip--${pay.tone}`}>{pay.chip}</span>
              {c.payments !== 'connected' && <Link className="yc-link" to="/settings/payout-account">{c.payments === 'not_set_up' ? 'Set up payments' : 'Check payments'}</Link>}
            </Choice>
          </>
        )}
      </section>
    </div>
  );
}

function YourChoices(props) {
  const selfEmployed = isSelfEmployedMe();
  return (
    <SettingsPageLayout activeTab="your-choices" {...props}>
      {selfEmployed ? <YourChoicesBody user={props.user} /> : (
        <div className="settings-card">
          <div className="settings-card-header">
            <h2>Your choices</h2>
            <p>This page is for self-employed stylists. Their choices are in Team, under Members, Self Employed, Their choices.</p>
          </div>
          <Link className="settings-btn-link" to="/staff/members">Go to Team</Link>
        </div>
      )}
    </SettingsPageLayout>
  );
}

function mapStateToProps(state) {
  return { ...state };
}

export default connect(mapStateToProps)(YourChoices);
